import React, { useState } from "react";
import {
  User,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  Calendar,
  Search,
  FileText,
  Edit,
} from "lucide-react";
import {
  IRepaymentHistoryItem,
  IRepaymentLog,
  IStatusCycle,
} from "../../models/commissionAndRepayment.model";
import { formatToDDMMYYYY } from "../../utils/formatDateUtils";
import Button, { ButtonType } from "../Assets/Button";
import SearchInput from "../Search/SearchInput";
import RepaymentLogsModal from "./RepaymentLogsModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  onFilterChange?: (
    startDate: string,
    endDate: string,
    search?: string,
    page?: number,
    status?: IStatusCycle,
  ) => void;
}

const ViewRepaymentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data,
  onFilterChange,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | "">("");
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<IRepaymentLog[]>([]);

  if (!isOpen) return null;

  const repayments = data?.repayments || [];
  const pagination = data?.pagination;

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatCurrency = (amount: number) =>
    `₹ ${amount.toLocaleString("en-IN")}`;

  const getStatusBadge = (status: string, label?: string) => {
    const base =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    if (status === "approved") {
      return (
        <span className={`${base} bg-emerald-100 text-emerald-800`}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved {label ? `by ${label}` : ""}
        </span>
      );
    }

    if (status === "rejected") {
      return (
        <span className={`${base} bg-red-100 text-red-800`}>
          <XCircle className="w-3 h-3 mr-1" />
          Rejected {label ? `by ${label}` : ""}
        </span>
      );
    }

    return (
      <span className={`${base} bg-amber-100 text-amber-800`}>
        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        Pending {label ? `by ${label}` : ""}
      </span>
    );
  };

  const handleApplyFilter = () => {
    if (onFilterChange && startDate && endDate) {
      onFilterChange(
        startDate,
        endDate,
        search,
        1,
        statusFilter as IStatusCycle,
      );
      setIsFilterApplied(true);
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setSearch("");
    setStatusFilter("");

    setIsFilterApplied(false);
    if (onFilterChange) {
      onFilterChange("", "");
    }
  };

  const handlePageChange = (newPage: number) => {
    if (onFilterChange) {
      onFilterChange(
        startDate,
        endDate,
        search,
        newPage,
        statusFilter as IStatusCycle,
      );
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    if (onFilterChange) {
      onFilterChange(startDate, endDate, search, 1, value as IStatusCycle);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white w-[880px] h-[70vh] overflow-y-auto rounded-xl shadow-xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-slate-800">
            Repayment Details
          </h2>

          <button
            onClick={onClose}
            className="p-2 text-white rounded-md text-sm"
          >
            <X className="w-4 h-4 text-gray-800" />
          </button>
        </div>

        <div className="mb-6 border border-slate-200 rounded-xl bg-slate-50 p-4 space-y-4">
          {/* Row 1: Search + Status */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative w-full md:w-1/2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <SearchInput
                placeholder="Search Customer Name..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md
        focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary
        outline-none transition-all text-sm text-gray-700 shadow-sm"
                onSearch={(value) => {
                  setSearch(value);
                  if (onFilterChange) {
                    onFilterChange(
                      startDate,
                      endDate,
                      value,
                      1,
                      statusFilter as IStatusCycle,
                    );
                  }
                }}
              />
            </div>

            <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200">
              {[
                { value: "", label: "All" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleStatusFilterChange(opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-md transition font-medium ${
                    statusFilter === opt.value
                      ? "bg-navyBlue-primary text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: Date Filter */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navyBlue-primary"
              />
              <span className="text-slate-500 text-xs">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navyBlue-primary"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleApplyFilter}
                disabled={!startDate || !endDate}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition ${
                  !startDate || !endDate
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : "bg-navyBlue-primary text-white hover:bg-navyBlue-light"
                }`}
              >
                Apply Filter
              </button>

              {isFilterApplied && (
                <Button
                  type={ButtonType.Cancel}
                  onClick={handleClearFilter}
                  className="text-xs !py-1.5"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {isFilterApplied && (
            <div className="text-xs text-slate-600">
              Showing results from {formatToDDMMYYYY(startDate)} to{" "}
              {formatToDDMMYYYY(endDate)}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {repayments.map((repayment: any) => {
            const history = repayment.repaymentHistory || [];
            const payment = repayment.payment || {};
            const isExpanded = expandedRows[repayment._id];

            const totalRepayment = history.reduce(
              (sum: number, item: any) => sum + (item.repaymentAmount || 0),
              0,
            );

            return (
              <div
                key={repayment._id}
                className="border border-slate-200 rounded-xl overflow-hidden"
              >
                <div
                  className="bg-slate-50 px-5 py-1 border-b cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleRow(repayment._id)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                        <User className="w-3 h-3 text-sky-600" />
                      </div>

                      <div>
                        <div className="text-xs font-semibold text-slate-800">
                          {payment.customerName}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-xs font-semibold text-slate-700">
                        Total Repayment:{" "}
                        <span className="text-sky-700">
                          {formatCurrency(totalRepayment)}
                        </span>
                      </div>

                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expandable Repayment History */}
                {isExpanded && (
                  <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {history.map((item: IRepaymentHistoryItem) => (
                      <div
                        key={item._id}
                        className="bg-white border border-slate-200 rounded-lg p-2 shadow-sm"
                      >
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">
                              Payment Amount
                            </span>
                            <span className="text-slate-800">
                              {formatCurrency(item.paymentAmount || 0)}
                            </span>
                          </div>

                          {item.fileCharge && (
                            <div className="flex justify-between text-gray-600">
                              <span>File Charge</span>
                              <span className="text-red-600">
                                - {formatCurrency(item.fileCharge)}
                              </span>
                            </div>
                          )}

                          {item.portalCharge && (
                            <div className="flex justify-between text-gray-600">
                              <span>
                                {item.totalPortalCharge
                                  ? `Portal Charge (${formatCurrency(item.portalCharge)} × ${item.plantSizeKW} kW)`
                                  : "Portal Charge"}
                              </span>
                              <span className="text-red-600">
                                -{" "}
                                {formatCurrency(
                                  item.totalPortalCharge ?? item.portalCharge,
                                )}
                              </span>
                            </div>
                          )}

                          {Array.isArray(item.customFields) &&
                            item.customFields.length > 0 &&
                            item.customFields.map((field, index) => (
                              <div
                                key={index}
                                className="flex justify-between gap-6"
                              >
                                <span className="text-slate-500 capitalize">
                                  {field.customFieldName}:
                                </span>
                                <span className="text-red-600">
                                  -{" "}
                                  {formatCurrency(field.customFieldValue || 0)}
                                </span>
                              </div>
                            ))}

                          <div className="border-t my-2"></div>

                          <div className="flex justify-between font-semibold">
                            <span>Repayment Amount</span>
                            <span className="text-slate-900">
                              {formatCurrency(item.repaymentAmount || 0)}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-700">
                            Applied At: {formatToDDMMYYYY(item.appliedAt)}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-col gap-2">
                          {getStatusBadge(
                            item.cseStatus,
                            repayment?.salesRep?.cseId?.username,
                          )}
                          {getStatusBadge(item.accountStatus, "Account")}
                        </div>
                        <div className="w-full flex items-center justify-between gap-1 mt-2">
                          {item.proof && (
                            <Button
                              type={ButtonType.Submit}
                              onClick={() => window.open(item.proof, "_blank")}
                              className="w-full inline-flex items-center justify-center gap-1 text-xs !py-1.5"
                            >
                              <FileText className="w-3 h-3" />
                              Proof
                            </Button>
                          )}
                          {item.logs && item.logs.length > 0 && (
                            <Button
                              type={ButtonType.Cancel}
                              onClick={() => {
                                setSelectedLogs(item.logs);
                                setLogsModalOpen(true);
                              }}
                              className="w-full inline-flex items-center justify-center gap-1 text-xs !py-1.5"
                            >
                              <Edit className="w-3 h-3" />
                              Logs
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {repayments.length === 0 && (
            <div className="text-center text-slate-500 py-10">
              No repayments found.
            </div>
          )}
        </div>
        {pagination && pagination.pages > 1 && (
          <div className="mt-6 flex justify-between items-center border-t border-slate-200 pt-4">
            <div className="text-sm text-slate-600">
              Showing page {pagination.page} of {pagination.pages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className={`px-3 py-1 text-sm rounded-md ${
                  pagination.hasPrevPage
                    ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    : "bg-slate-50 text-slate-400 cursor-not-allowed"
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className={`px-3 py-1 text-sm rounded-md ${
                  pagination.hasNextPage
                    ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    : "bg-slate-50 text-slate-400 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <RepaymentLogsModal
        open={logsModalOpen}
        logs={selectedLogs}
        onClose={() => setLogsModalOpen(false)}
      />
    </div>
  );
};

export default ViewRepaymentModal;
